import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import CardSelector from '../components/CardSelector';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import Header from '../components/Header';

interface Card {
    id: number;
    holder_name: string;
    last4: string;
    createdAt: string;
}

const CardSelectionPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [hasSelectedCard, setHasSelectedCard] = useState<boolean | null>(null);
    const [cards, setCards] = useState<Card[]>([]);
    const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
    const [defaultCard, setDefaultCard] = useState<Card | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkingCard, setCheckingCard] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!token) {
            setError('Access token is required');
            setCheckingCard(false);
            return;
        }
        checkHasSelectedCard();
    }, [token]);

    const checkHasSelectedCard = async () => {
        if (!token) return;

        // Check if user is explicitly changing their card (coming from wallet page)
        const changeCard = searchParams.get('changeCard');

        if (changeCard === 'true') {
            // Skip the check and load cards directly for card change
            setCheckingCard(false);
            setHasSelectedCard(false);
            await loadCards();
            return;
        }

        try {
            setCheckingCard(true);
            setError('');
            const response = await apiService.hasSelectedCard(token);

            if (response.has_selected_card && response.card_details) {
                // User has a default card, store it and navigate to wallet page
                setHasSelectedCard(true);
                setDefaultCard(response.card_details);
                navigate(`/wallet?token=${encodeURIComponent(token)}`);
            } else {
                // User needs to select a card
                setHasSelectedCard(false);
                await loadCards();
            }
        } catch (err: any) {
            setError(err.message || 'Failed to check selected card');
        } finally {
            setCheckingCard(false);
        }
    };

    const loadCards = async () => {
        if (!token) return;

        try {
            setLoading(true);
            setError('');
            const cardsData = await apiService.listCards(token);
            setCards(cardsData);
        } catch (err: any) {
            setError(err.message || 'Failed to load cards');
        } finally {
            setLoading(false);
        }
    };

    const handleCardSelect = (cardId: number) => {
        setSelectedCardId(cardId);
    };

    const handleSelectCard = async () => {
        if (!selectedCardId || !token) return;

        try {
            setLoading(true);
            setError('');
            await apiService.setDefaultCard(token, selectedCardId);
            // Get updated card details after setting default card
            const response = await apiService.hasSelectedCard(token);
            if (response.card_details) {
                setDefaultCard(response.card_details);
            }
            // Navigate to wallet page after successful card selection
            navigate(`/wallet?token=${encodeURIComponent(token)}`);
        } catch (err: any) {
            setError(err.message || 'Failed to set default card');
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        setError('');
        if (hasSelectedCard === null || hasSelectedCard === true) {
            checkHasSelectedCard();
        } else {
            loadCards();
        }
    };

    const handleBackToApp = () => {
        // Try to close the webview or navigate back to the mobile app
        if (window.history.length > 1) {
            window.history.back();
        } else {
            // Fallback: try to close the window/tab
            window.close();
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

                <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                            <p className="text-gray-600">A valid access token is required to access this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (checkingCard) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">


            <div className="container mx-auto px-4 py-6 max-w-4xl">
                {error && (
                    <div className="mb-6">
                        <ErrorMessage message={error} onClose={() => setError('')} />
                        <button
                            onClick={handleRetry}
                            className="mt-4 text-white px-6 py-3 rounded-xl hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-blue-200 font-medium shadow-lg transition-all duration-200 transform hover:scale-105"
                            style={{ backgroundColor: '#1d4df0' }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!error && !checkingCard && hasSelectedCard === false && (
                    <>
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Your Default Card</h3>
                                    <p className="text-gray-700">
                                        Direct deposits to wallets are stateless transactions. Please select a default card
                                        for your wallet deposits. All deposits to your dedicated wallet address will be
                                        credited to the selected card by default.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <CardSelector
                            cards={cards}
                            selectedCardId={selectedCardId}
                            onCardSelect={handleCardSelect}
                            onSelectCard={handleSelectCard}
                            loading={loading}
                        />
                        {loading && <LoadingSpinner />}
                    </>
                )}
            </div>
        </div>
    );
};

export default CardSelectionPage;

